import React, { useState, useContext, useEffect, useRef } from "react";
import { StyleContext } from "../providers/StyleContextProvider";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { SummaryOfCompletion } from "../components/VacancyPosting/SummaryOfCompletion";
import { AuthContext } from '../providers/AuthContextProvider';
import { LoggedRecruiter } from '../models/LoggedRecruiter';
import { useMediaQuery } from 'react-responsive';
import { createSearchParams, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Footer } from "../components/Shared/Footer";
import { SummaryOfCompletionModel } from "../models/SummaryOfCompletionModel";
import { ProfileEntrance } from "../components/Home/ProfileEntrance";
import { Button } from "../components/Shared/Buttons/Button";
import { ReactComponent as LeftArrow } from '../assets/svg/left_arrow.svg';
import { ReactComponent as TickMark } from '../assets/svg/tick_circles.svg';
import { 
    getPreApplicationQuestions, 
    getSMSService, 
    getStageEmailInitials, 
    getVacancy, 
    getVacancyDetails, 
    getVacancyQuestionInitials, 
    editCVLessQuestions, 
    editReplyMails, 
    postVacancy, 
    editVacancyQuestions, 
    submitVacancy
} from "../services/VacancyService";
import { ResponseModel } from "../models/ResponseModel";
import useErrorPage from "../hooks/useErrorPage";
import { PopUpWindow } from "../components/Shared/PopUpWindow";
import { useAlert } from "../hooks/useAlert";
import { 
    SMSServiceModel, 
    VacancyPostingModel, 
    VacancyEditModel,
    VacancyEditResponse, 
    VacancyQuestionModel
} from "../models/VacancyPostingModel";
import { BasicDetailsEdit } from "../components/VacancyPosting/BasicDetailsEdit";
import { VacancyQuestionsEdit } from "../components/VacancyPosting/VacancyQuestionsEdit";
import { CVLessQuestionsEdit } from "../components/VacancyPosting/CVLessQuestionsEdit";
import { ReplyMailEdit } from "../components/VacancyPosting/ReplyMailEdit";
import { StageEmailModel } from "../models/JobApplicationStageEmail";
import { JobEditPageLoader } from "../components/Loaders/JobEditPageLoader";
import { OutsideWorkingHoursNotification } from "../helpers/LayoutTexts";
import { isOutsideOfficeHours } from "../helpers/Common";

export const VacancyEditPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const jobId = params.jobId ? Number(params.jobId) : undefined;
    const authContext = useContext(AuthContext);
    const touchedTabsRef = useRef<Set<string>>(new Set());
    const currentTabRef = useRef<{ saveTab: () => Promise<any> } | null>(null);
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("vacancyPostingPage"));    
    const [currentTab, setCurrentTab] = useState<string>("basicDetails");
    const organizationId: number = searchParams.get("orgId") === null ? 0 : +searchParams.get("orgId")!;
    const [isCVLess, setIsCVLess] = useState<boolean>(false);    
    const [basicInfoModel, setBasicInfoModel] = useState<VacancyPostingModel>();
    const [vacancyQuestionModel, setVacancyQuestionModel] = useState<VacancyQuestionModel>();
    const [preApplicationQuestion, setPreApplicationQuestion] = useState<VacancyQuestionModel>();
    const [smsService, setSMSService] = useState<SMSServiceModel>();
    const [stageEmails, setStateEmails] = useState<StageEmailModel>();
    const [jobImageFile, setJobImageFile] = useState<File | null>(null);
    const [updatedData, setUpdatedData] = useState<VacancyEditResponse>();
    const [isOutsideWorkingHours, setIsOutsideWorkingHours] = useState<boolean>(false);
    const [completionModel, setCompletionModel] = useState<SummaryOfCompletionModel>({
        basicInfo: true,
        required: true,
        salary: false,
        description: false,
        vacancyQuestions: false,
        passScore: false,
        cvLessQuestion: false,
        service: false,
        question: true,
        replyMail: false,
        optional: false,
        submission: true,
        submit: true
    });
    const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { Alert, openAlert } = useAlert();
    const isSubmittingRef = useRef(false);
    
    useEffect(() => {   
        checkWorkingHours();                      
        if(recruiter === null) {  
            const params = { callbackUrl: location.pathname + location.search, message: "Please login or register to edit a job." };
            navigate({
                pathname: '/recruiter/account/login',
                search: `?${createSearchParams(params)}`
            });
        } 
        else {
            getInitialVacancyDetails();
        }
    }, []);

    useEffect(() => {         
        setStyles(styleContext.getComponentStyle("vacancyPostingPage"));
    }, [isMobile]);

    function checkWorkingHours() {
        setIsOutsideWorkingHours(isOutsideOfficeHours());
    }

    async function getBasicDetails(): Promise<VacancyPostingModel> {    
        setIsLoading(true);       
        if (!basicInfoModel) {
            const jobDetails = await getVacancy(recruiter.recruiterId, recruiter.token, jobId, organizationId);
            setIsLoading(false);
            setBasicInfoModel(jobDetails);
            return jobDetails;
        }
        else {
            setIsLoading(false);
            return basicInfoModel;
        }        
    }

    async function getVacncyQuestionDetails(): Promise<VacancyEditResponse> {
        if (!vacancyQuestionModel) {
            const vacancyQuestion = await getVacancyQuestionInitials(recruiter.recruiterId, recruiter.token, jobId);
            setVacancyQuestionModel(vacancyQuestion);

            const initialLoad: VacancyEditResponse = {
                ...updatedData,
                jobId: vacancyQuestion.jobId,
                recruiterId: recruiter.recruiterId,      
                questionTypes: vacancyQuestion.questionTypes,
                questionList: vacancyQuestion.jobQuestions,
                screenAutomatically: vacancyQuestion.screenAutomatically,
                passScore: vacancyQuestion.passScore,
                updatedQuestionList: [],
                deletedQuestionIds: []
            };
            
            setUpdatedData(initialLoad);
            return initialLoad;
        }
        else {
            return updatedData!;
        }
    }

    async function getCVLessQuestionDetails(): Promise<VacancyEditResponse> {
        
        let currentVacancy: VacancyEditResponse = updatedData || {} as VacancyEditResponse;

        if (!preApplicationQuestion) {
            const fetchedData = await getPreApplicationQuestions(recruiter.recruiterId, recruiter.token, jobId);
            currentVacancy = {
                 ...currentVacancy,
                jobId: fetchedData.jobId,
                recruiterId: recruiter.recruiterId,
                questionTypes: fetchedData.questionTypes,
                preApplicationQuestionList: fetchedData.preApplicationQuestions,
                emailServiceStatus: fetchedData.emailStatus ?? false,
                updatedQuestionList: [],
                deletedQuestionIds: []
            };
            setPreApplicationQuestion(fetchedData);
        }

        if (!smsService) {
            const fetchedSMS = await getSMSService(recruiter.recruiterId, jobId);
            setSMSService(fetchedSMS);
            currentVacancy = {
                ...currentVacancy,
                smsServiceStatus: (fetchedSMS?.message != null && fetchedSMS.message.length > 0),
                smsService: fetchedSMS
            };
        }

        setUpdatedData(currentVacancy);
        return currentVacancy;
    }

    async function getReplyMailDetails(): Promise<VacancyEditResponse> {
        if (!stageEmails) {
            const emails = await getStageEmailInitials(jobId);
            setStateEmails(emails);
            
            const initialLoad: VacancyEditResponse = {
                ...updatedData,                                     
                jobId: jobId!,
                recruiterId: recruiter.recruiterId,
                emailList: emails.emailTemplates,
                emailApplicationStatues: emails.applicationStatuses,      
                updatedEmailList: [],
                deletedEmailIds: []
            };

            setUpdatedData(initialLoad);
            return initialLoad;
        } 
        else {
            return updatedData!;
        }
    }

    async function getInitialVacancyDetails() {
        try {
            const model: VacancyEditModel = await getVacancyDetails(recruiter.recruiterId, recruiter.token, jobId);
            setIsCVLess(model.job.isPreApplicationFormEnabled);
            setCompletionModel(prev => ({
                ...prev,
                salary: (model.job.minimumSalary > 0) || (model.job.maximumSalary > 0),
                description: !!model.job.jobDescription,
                passScore: (model.job.passScore ?? 0) > 0,
                cvLessQuestion: model.job.isPreApplicationFormEnabled,
                service: model.smsServiceEnable,
                optional: model.hasEmail
            }));
        } 
        catch (error) {
            toErrorPage(error);
        }
    }

    const handleTabChange = (nextTab: string) => {
        setCurrentTab(nextTab);
    };

    const saveActiveTabToState = async (): Promise<{ ok: boolean; data?: any; file?: File | null }> => {
        if (!currentTabRef.current?.saveTab) return { ok: true };

        const result = await currentTabRef.current.saveTab();
        if (!result) return { ok: false }; 

        touchedTabsRef.current.add(currentTab);
        return { ok: true, data: result.data, file: (result as any).file };
    };

    const handleTabClick = async (tab: string) => {
        if (currentTab === tab) return;
        const { ok } = await saveActiveTabToState();
        if (!ok) return;
        setCurrentTab(tab);
    };

    const handleBack = () => {
        navigate(-1);
    }

    const handleBasicDetailUpdate = (updatedData: VacancyPostingModel, imageFile?: File | null) => {
        setBasicInfoModel(prev => ({
            ...prev,         
            ...updatedData   
        }));

        if (imageFile !== undefined) {
            setJobImageFile(imageFile);
        }
    }

    const handleVacancyQuestionUpdate = (model: VacancyEditResponse) => {
        setUpdatedData(prev => ({
            ...prev,
            ...model
        }));
    };

    const handleCVLessQuestionUpdate = (model: VacancyEditResponse) => {
        setUpdatedData(prev => ({
            ...prev,
            ...model
        }));
    };

    const handleEmailsUpdate = (model: VacancyEditResponse) => {
        setUpdatedData(prev => ({
            ...prev,
            ...model
        }));
    };

    async function handleSubmit(event?: React.FormEvent<HTMLFormElement>) {        
        event?.preventDefault();
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        try {
            const activeSave = await saveActiveTabToState();
            if (!activeSave.ok) return; 
            if (touchedTabsRef.current.size === 0) return;

            if (touchedTabsRef.current.has("basicDetails")) {
                const data = currentTab === "basicDetails" ? activeSave.data : basicInfoModel;
                const file = currentTab === "basicDetails" ? activeSave.file : jobImageFile;
                await saveBasicDetails(data, file);
            }
            if (touchedTabsRef.current.has("vacancyQuestions")) {
                const data = currentTab === "vacancyQuestions" ? activeSave.data : updatedData;
                saveVacancyQuestions(data);
            }
            if (touchedTabsRef.current.has("cvLessQuestions")) {
                const data = currentTab === "cvLessQuestions" ? activeSave.data : updatedData;
                saveCVLessQuestions(data);
            }
            if (touchedTabsRef.current.has("replyToMail")) {
                const data = currentTab === "replyToMail" ? activeSave.data : updatedData;
                saveReplyMails(data);
            }

            const response = await submitVacancy(recruiter.recruiterId, recruiter.token, jobId);
            if (response.status) {
                setOpenMessageDialog(true);
            } else {
                openAlert("error", "There was a problem with your submission. Please try again later.");
            }
        }
        catch (error) { 
            toErrorPage(error);
        }
         finally {
            isSubmittingRef.current = false;
        }
    }

    async function saveBasicDetails(directData?: VacancyPostingModel, directFile?: File | null) {
        const dataToSave = directData ?? basicInfoModel;
        const fileToSave = directFile !== undefined ? directFile : jobImageFile;
        if (!dataToSave) return;
        
        const formData = new FormData();
        formData.append("ModelString", JSON.stringify(dataToSave)); 
        if (fileToSave) formData.append("Attachment", fileToSave);

        await postVacancy(formData, recruiter.recruiterId, recruiter.token)
        .then((response: ResponseModel) => {
            if(!response.status) {
                openAlert("error", "There was a problem with Basic Info submission. Please try again later.");
            }
        })
        .catch((error) => {
            toErrorPage(error);
        })         
    }

    async function saveVacancyQuestions(directData?: VacancyEditResponse) {
        const dataToSave = directData ?? updatedData;
        if (!dataToSave) return;
        await editVacancyQuestions(dataToSave, recruiter.token)
        .then((response: ResponseModel) => {
            if(!response.status) {
                openAlert("error", "There was a problem with Basic Info submission. Please try again later.");
            }
        })
        .catch((error) => {
            toErrorPage(error);
        })         
    }

    async function saveCVLessQuestions(directData?: VacancyEditResponse) {
        const dataToSave = directData ?? updatedData;
        if (!dataToSave) return;

        await editCVLessQuestions(dataToSave, recruiter.token)
        .then((response: ResponseModel) => {
            if(!response.status) {
                openAlert("error", "There was a problem with question submission. Please try again later.");
            }
        })
        .catch((error) => {
            toErrorPage(error);
        })         
    }

    async function saveReplyMails(directData?: VacancyEditResponse) {
        const dataToSave = directData ?? updatedData;
        if (!dataToSave) return;

        await editReplyMails(dataToSave, recruiter.token)
        .then((response: ResponseModel) => {
            if(!response.status) {
                openAlert("error", "There was a problem with reply mail submission. Please try again later.");
            }
        })
        .catch((error) => {
            toErrorPage(error);
        })         
    }

    const handleViewJobs = () => {
        setOpenMessageDialog(false);
        navigate('/recruiter/vacancy?page=1&status=PENDING');
    };

    const handleSummaryUpdate = (modelUpdates?: Partial<SummaryOfCompletionModel>) => {
        if (modelUpdates) {
            setCompletionModel(prev => {
                const newModel = { ...prev, ...modelUpdates };
                
                if (modelUpdates.cvLessQuestion !== undefined) {
                    setIsCVLess(modelUpdates.cvLessQuestion);
                }
                
                return newModel;
            });
        }
    };

    const previewVacancy = () => {
        if (jobId) {
            window.open(`/recruiter/preview/${jobId}`, "_blank");
        }
    };
    return(
        <>
            {(!isMobile && isOutsideWorkingHours) && <div style={styles.workingHoursReminder}>{OutsideWorkingHoursNotification.text}</div>}
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <RecruiterMenu>
                        <div style={styles.topic}>
                            {"Edit Job Post"}
                        </div>
                    </RecruiterMenu>
                </div>
                {(isMobile && isOutsideWorkingHours) && <div style={styles.workingHoursReminder}>{OutsideWorkingHoursNotification.text}</div>}
                <div style={styles.rightWrapper}>
                    <div style={styles.pageHeader}>
                        <div style={styles.buttonWrapper}>
                            <Button style={styles.backButton} onClick={handleBack}>
                                <LeftArrow style={styles.buttonIcon} />
                            </Button>
                            <span style={styles.buttonTittle}>{"Back"}</span>
                        </div>
                        <div style={styles.profileWrapper}><ProfileEntrance recruiter={recruiter} /></div>
                    </div>
                    {!isMobile && <div style={styles.topic}>{"Edit Job Post"}</div>}
                    <div style={styles.innerRightWrapper}>
                        <SummaryOfCompletion styles={styles.summaryOfCompletion} model={completionModel} editStage={true} onPreview={previewVacancy} onSubmit={handleSubmit}/>
                        {isLoading ? <JobEditPageLoader/> :<div style={styles.readyToSubmit.mainWrapper}>
                            {!isMobile && <div style={styles.readyToSubmit.subTopic}>{"Once it’s approved, your advert will go live"}</div>}
                            <div style={styles.readyToSubmit.tabsWrapper}>
                                <div style={styles.readyToSubmit.tabs}>
                                    <Button 
                                        type="button" 
                                        style={{...styles.readyToSubmit.tab, ...(currentTab === "basicDetails" && styles.readyToSubmit.tabActive)}} 
                                        onClick={() => handleTabClick("basicDetails")}
                                    >
                                        {"Basic Info"}
                                    </Button>
                                    {isCVLess ? <Button 
                                        type="button" 
                                        style={{...styles.readyToSubmit.tab, ...(currentTab === "cvLessQuestions" && styles.readyToSubmit.tabActive)}} 
                                        onClick={() => handleTabClick("cvLessQuestions")}
                                    >
                                        {"CV Less Questions"}
                                    </Button>
                                    : <Button 
                                        type="button" 
                                        style={{...styles.readyToSubmit.tab, ...(currentTab === "vacancyQuestions" && styles.readyToSubmit.tabActive)}} 
                                        onClick={() => handleTabClick("vacancyQuestions")}
                                    >
                                        {"Vacancy Questions"}
                                    </Button>}
                                    <Button 
                                        type="button" 
                                        style={{...styles.readyToSubmit.tab, ...(currentTab === "replyToMail" && styles.readyToSubmit.tabActive)}} 
                                        onClick={() => handleTabClick("replyToMail")}
                                    >
                                        {"Reply Mail"}
                                    </Button>
                                </div>
                            </div>
                            {currentTab === "basicDetails" && 
                                <BasicDetailsEdit 
                                    ref={currentTabRef}
                                    styles={styles.basicDetails}
                                    jobId={jobId} 
                                    initialImageFile={jobImageFile}
                                    onUpdateSummary={handleSummaryUpdate} 
                                    onTabChange={handleTabChange}                                      
                                    onLoad={getBasicDetails} 
                                    onSave={handleBasicDetailUpdate}
                                />}
                            {currentTab === "vacancyQuestions" && 
                                <VacancyQuestionsEdit
                                    ref={currentTabRef}                                   
                                    styles={styles.vacancyQuestion} 
                                    jobId={jobId}
                                    onUpdateSummary={handleSummaryUpdate} 
                                    onTabChange={handleTabChange}                                      
                                    onLoad={getVacncyQuestionDetails} 
                                    onSave={handleVacancyQuestionUpdate}
                                />}
                            {currentTab === "cvLessQuestions" && 
                                <CVLessQuestionsEdit 
                                    ref={currentTabRef}                                    
                                    styles={styles.cvLessQuestion}
                                    jobId={jobId} 
                                    onUpdateSummary={handleSummaryUpdate} 
                                    onTabChange={handleTabChange}                                      
                                    onLoad={getCVLessQuestionDetails} 
                                    onSave={handleCVLessQuestionUpdate}
                                />}
                            {currentTab === "replyToMail" && 
                                <ReplyMailEdit 
                                    ref={currentTabRef}
                                    styles={styles.replyMail}
                                    jobId={jobId} 
                                    onUpdateSummary={handleSummaryUpdate} 
                                    onTabChange={handleTabChange}                                      
                                    onLoad={getReplyMailDetails} 
                                    onSave={handleEmailsUpdate}
                                />}
                            <hr style={styles.readyToSubmit.line}/>
                            <div style={styles.readyToSubmit.buttonsWrapper}>
                                <Button style={styles.readyToSubmit.buttonsWrapper.back} onClick={handleBack}>{"BACK"}</Button>
                                <Button style={styles.readyToSubmit.buttonsWrapper.next} disabled={isSubmittingRef.current} onClick={handleSubmit}>{"SUBMIT"}</Button>          
                            </div> 
                            <PopUpWindow styles={styles.readyToSubmit.jobSubmisionDialog} open={openMessageDialog} toggle={handleViewJobs} closeCircle={true}>
                                <TickMark style={styles.readyToSubmit.tickMark}/>
                                <h1 style={styles.readyToSubmit.deleteDialogTitle}>{"Job successfully updated to the system"}</h1>
                                <p style={styles.readyToSubmit.popupText}>
                                    {"Your Job is being processed by the Xpressjobs team and will be posted on the site shortly."}
                                </p>
                                <div className="center-content">
                                    <Button style={{...styles.readyToSubmit.jobViewButton, ...styles.readyToSubmit.jobViewButton.blue}} onClick={handleViewJobs}>
                                        {"VIEW ALL MY JOBS"}
                                    </Button> 
                                </div>
                            </PopUpWindow>
                            <Alert/>
                        </div>}
                    </div>
                </div>
            </div>
            <Footer/>
        </>    
    )
}
